// The filter controls above the Library list: Show (all, favourites, my objects, built-in), the pump
// controls, the pipe fitting controls, the count line and "Clear filters". The rules are in
// library/filter.ts; this file draws them. A control of a kind shows only when the list has that kind.
import type { ComponentChildren } from "preact";
import {
  NO_FILTER,
  SHOW_LABEL,
  fittingKindLabel,
  isFiltering,
  viewLabel,
  type FacetKey,
  type FacetOptions,
  type FilterState,
  type Opt,
  type RowKind,
  type ShowMode
} from "../library/filter";

const SHOW_MODES: ShowMode[] = ["all", "favourites", "mine", "builtin"];

export interface LibraryFiltersProps {
  state: FilterState;
  options: FacetOptions;
  /** How many objects of each kind are in the list (before any filter). */
  present: Record<RowKind, number>;
  /** The count line, from describeCounts. */
  counts: string;
  onChange(next: FilterState): void;
}

function Chip({ label, on, disabled, filter, value, title, onPress }: { label: string; on: boolean; disabled?: boolean; filter: string; value: string; title?: string; onPress(): void }) {
  return (
    <button type="button" class={`lf-chip${on ? " on" : ""}`} aria-pressed={on} disabled={disabled} data-filter={filter} data-value={value} title={title} onClick={onPress}>
      {label}
    </button>
  );
}

function Group({ name, children }: { name: string; children: ComponentChildren }) {
  return (
    <div class="lf-group" role="group" aria-label={name}>
      {children}
    </div>
  );
}

const CHIP_TITLE: Record<string, string> = {
  series: "Pump series",
  poles: "Motor poles",
  view: "View of the pump",
  fittingKind: "Kind of pipe fitting"
};

function Pick<V extends string | number>({
  id,
  label,
  name,
  value,
  options,
  format,
  onPick
}: {
  id: string;
  /** The short word beside the list. */
  label: string;
  /** The full name, for a screen reader. */
  name: string;
  value: V | null;
  options: Array<Opt<V>>;
  format(v: V): string;
  onPick(v: V | null): void;
}) {
  return (
    <label class="lf-field" for={id}>
      <span>{label}</span>
      <select
        id={id}
        aria-label={name}
        value={value === null ? "" : String(value)}
        onChange={(e) => {
          const raw = (e.currentTarget as HTMLSelectElement).value;
          if (raw === "") return onPick(null);
          onPick((options.find((o) => String(o.value) === raw)?.value ?? null) as V | null);
        }}
      >
        <option value="">Any</option>
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)} disabled={o.count === 0 && o.value !== value}>
            {format(o.value)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function LibraryFilters({ state, options, present, counts, onChange }: LibraryFiltersProps) {
  const set = <K extends FacetKey>(key: K, value: FilterState[K]) => onChange({ ...state, [key]: state[key] === value ? null : value });
  const put = <K extends FacetKey>(key: K, value: FilterState[K]) => onChange({ ...state, [key]: value });
  const on = isFiltering(state);

  const chips = <K extends "series" | "poles" | "view" | "fittingKind">(key: K, label: (v: Opt<string | number>["value"]) => string) =>
    options[key].map((o) => (
      <Chip
        key={String(o.value)}
        label={label(o.value)}
        on={state[key] === o.value}
        disabled={o.count === 0 && state[key] !== o.value}
        filter={key}
        value={String(o.value)}
        title={`${CHIP_TITLE[key]}: ${label(o.value)} (${o.count} with the other filters)`}
        onPress={() => set(key, o.value as FilterState[K])}
      />
    ));

  return (
    // data-own-keys: letters and digits typed in these controls are NOT drawing shortcuts.
    <div class="library-filters" data-own-keys="">
      <div class="lf-row">
        <Group name="Show">
          {SHOW_MODES.map((m) => (
            <Chip key={m} label={SHOW_LABEL[m]} on={state.show === m} filter="show" value={m} onPress={() => onChange({ ...state, show: m })} />
          ))}
        </Group>
      </div>
      {present.pump > 0 && (
        <>
          <div class="lf-row">
            <Group name="Pump series">{chips("series", (v) => String(v))}</Group>
            <Group name="Motor poles">{chips("poles", (v) => `${v} pole`)}</Group>
            <Group name="Pump view">{chips("view", (v) => viewLabel(String(v)))}</Group>
          </div>
          <div class="lf-row lf-selects">
            <Pick id="lf-inlet" label="Inlet" name="Inlet DN" value={state.inletDn} options={options.inletDn} format={(v) => `DN${v}`} onPick={(v) => put("inletDn", v)} />
            <Pick id="lf-outlet" label="Outlet" name="Outlet DN" value={state.outletDn} options={options.outletDn} format={(v) => `DN${v}`} onPick={(v) => put("outletDn", v)} />
            <Pick id="lf-kw" label="Motor" name="Motor power" value={state.kW} options={options.kW} format={(v) => `${v} kW`} onPick={(v) => put("kW", v)} />
          </div>
        </>
      )}
      {present.fitting > 0 && (
        <div class="lf-row">
          <Group name="Pipe fitting kind">{chips("fittingKind", (v) => fittingKindLabel(String(v)))}</Group>
          <Pick id="lf-fit-size" label="Size" name="Pipe fitting size" value={state.fittingSize} options={options.fittingSize} format={(v) => `${v} mm`} onPick={(v) => put("fittingSize", v)} />
        </div>
      )}
      <div class="lf-foot">
        <span class="library-count" role="status" aria-live="polite">
          {counts}
        </span>
        <button type="button" class="lf-clear" disabled={!on} onClick={() => onChange(NO_FILTER)}>
          Clear filters
        </button>
      </div>
    </div>
  );
}
